import { CaretRight, Star, Umbrella, Vault } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { PotTermHint } from './PotTermHint';

function EmptyWayCard({ to, icon: Icon, title, subtitle, tone = 'dream' }) {
  const iconTone =
    tone === 'duty'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
      : 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300';

  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-[0.9375rem] border border-border bg-card px-3.5 py-3.5 transition-colors hover:bg-muted/40"
    >
      <span
        className={`flex h-[2.375rem] w-[2.375rem] shrink-0 items-center justify-center rounded-[0.6875rem] ${iconTone}`}
      >
        <Icon className="h-[1.2rem] w-[1.2rem]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">{subtitle}</p>
      </div>
      <span className="flex h-[1.875rem] w-[1.875rem] shrink-0 items-center justify-center rounded-[0.5625rem] bg-sage-600 text-white">
        <CaretRight className="h-4 w-4" aria-hidden />
      </span>
    </Link>
  );
}

export function PotsEmptyState() {
  return (
    <div className="flex flex-col gap-4">
      <div className="relative overflow-hidden rounded-[1.375rem]">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#6E7E63] to-[#7C93A8]"
          aria-hidden
        />
        <span
          className="absolute -right-5 -top-8 h-[9.375rem] w-[9.375rem] rounded-full bg-white/10"
          aria-hidden
        />
        <span
          className="absolute -bottom-8 left-5 h-[7.5rem] w-[7.5rem] rounded-full bg-white/10"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(20,22,18,0.55)] via-transparent to-transparent" aria-hidden />
        <div className="relative px-5 pb-5 pt-6">
          <Vault className="mb-4 h-[1.875rem] w-[1.875rem] text-white" aria-hidden />
          <h2 className="max-w-[16rem] font-display text-xl font-bold leading-tight text-white text-balance">
            {pt.pots.empty.hero}
          </h2>
        </div>
      </div>

      <p className="px-1 text-[13px] leading-relaxed text-muted-foreground">
        {pt.pots.empty.bodyBefore}
        <strong className="inline-flex items-center gap-1 font-bold text-sage-700 dark:text-sage-300">
          {pt.pots.empty.bodyHighlight}
          <PotTermHint term="cofre" variant="icon" />
        </strong>
        {pt.pots.empty.bodyAfter}
      </p>

      <div className="flex items-start gap-2.5 rounded-[0.875rem] border border-sage-100 bg-sage-50 px-3.5 py-3 text-xs leading-relaxed text-sage-700 dark:border-sage-900/50 dark:bg-sage-950/30 dark:text-sage-200">
        <Vault className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>{pt.pots.empty.bornNote}</p>
      </div>

      <div className="flex flex-col gap-2.5">
        <EmptyWayCard
          to={paths.goalsNew}
          icon={Star}
          title={pt.pots.empty.ctaGoals}
          subtitle={pt.pots.empty.ctaGoalsHint}
          tone="dream"
        />
        <EmptyWayCard
          to={paths.calendar}
          icon={Umbrella}
          title={pt.pots.empty.ctaCalendar}
          subtitle={pt.pots.empty.ctaCalendarHint}
          tone="duty"
        />
      </div>
    </div>
  );
}
